import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, CircularProgress, Snackbar, Alert, Switch, FormControlLabel,
  Divider, Avatar, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { getCollection, addDocument, updateDocument, deleteDocument } from '../../services/firestoreService';

const EMPTY_FORM = {
  nombre: '', apellidos: '', rut: '', specialtyIds: [], clinicIds: [], active: true,
};

const ManageProfessionals = () => {
  const [professionals, setProfessionals] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [busqueda, setBusqueda] = useState('');

  const professionalsFiltrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return professionals;
    return professionals.filter((p) => {
      const nombre = `${p.nombre || ''} ${p.apellidos || ''}`.toLowerCase();
      return nombre.includes(q);
    });
  }, [professionals, busqueda]);

  const load = async () => {
    setLoading(true);
    try {
      const [profsData, specsData, clinicsData] = await Promise.all([
        getCollection('professionals'),
        getCollection('specialties'),
        getCollection('clinics'),
      ]);
      setProfessionals(profsData.sort((a, b) => (a.apellidos || '').localeCompare(b.apellidos || '')));
      setSpecialties(specsData.filter((s) => s.active));
      setClinics(clinicsData.filter((c) => c.active));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (prof) => {
    setEditingId(prof.id);
    setForm({
      nombre: prof.nombre || '',
      apellidos: prof.apellidos || '',
      rut: prof.rut || '',
      specialtyIds: prof.specialtyIds || (prof.specialtyId ? [prof.specialtyId] : []),
      clinicIds: prof.clinicIds || (prof.clinicId ? [prof.clinicId] : []),
      active: prof.active !== false,
    });
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.nombre.trim() || !form.apellidos.trim()) {
      setSnackbar({ open: true, message: 'Nombre y apellidos son requeridos', severity: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const selectedSpecialties = specialties.filter((s) => form.specialtyIds.includes(s.id));
      const selectedClinics = clinics.filter((c) => form.clinicIds.includes(c.id));
      const data = {
        ...form,
        specialtyNames: selectedSpecialties.map((s) => s.name),
        clinicNames: selectedClinics.map((c) => c.name),
        fullName: `${form.nombre.trim()} ${form.apellidos.trim()}`,
      };

      if (editingId) {
        await updateDocument('professionals', editingId, data);
        setSnackbar({ open: true, message: 'Profesional actualizado', severity: 'success' });
      } else {
        await addDocument('professionals', data);
        setSnackbar({ open: true, message: 'Profesional creado', severity: 'success' });
      }
      handleClose();
      load();
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al guardar', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este profesional?')) return;
    await deleteDocument('professionals', id);
    setSnackbar({ open: true, message: 'Profesional eliminado', severity: 'info' });
    load();
  };

  const handleExportExcel = () => {
    const rows = professionals.map((p) => ({
      professionalId: p.id || '',
      fullName: p.fullName || `${p.nombre || ''} ${p.apellidos || ''}`.trim(),
      specialtyIds: (p.specialtyIds || (p.specialtyId ? [p.specialtyId] : [])).join(', '),
      specialtyNames: (p.specialtyNames || (p.specialtyName ? [p.specialtyName] : [])).join(', '),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Profesionales');
    XLSX.writeFile(wb, 'profesionales.xlsx');
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Profesionales Médicos</Typography>
          <Typography variant="body2" color="text.secondary">
            Médicos disponibles para agendamiento. Configura su agenda en la pestaña "Agenda de Profesionales".
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar por nombre"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            sx={{ minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportExcel}
            disabled={professionals.length === 0}
            sx={{ borderRadius: 3 }}
          >
            Excel
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 3 }}>
            Agregar Profesional
          </Button>
        </Box>
      </Box>
      {professionals.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No hay profesionales registrados</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={openCreate} sx={{ mt: 2 }}>
            Agregar el primer profesional
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell><strong>Nombre</strong></TableCell>
                <TableCell><strong>RUT</strong></TableCell>
                <TableCell><strong>Especialidad</strong></TableCell>
                <TableCell><strong>Clínica</strong></TableCell>
                <TableCell align="center"><strong>Estado</strong></TableCell>
                <TableCell align="center"><strong>Acciones</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {professionalsFiltrados.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {busqueda.trim()
                      ? 'No hay profesionales que coincidan con la búsqueda.'
                      : 'No hay profesionales registrados.'}
                  </TableCell>
                </TableRow>
              ) : professionalsFiltrados.map((prof) => (
                <TableRow key={prof.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light', fontSize: '0.8rem' }}>
                        {(prof.nombre || '?')[0]}{(prof.apellidos || '')[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Dr/a. {prof.nombre} {prof.apellidos}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{prof.rut || '—'}</TableCell>
                  <TableCell>
                    {prof.specialtyNames && prof.specialtyNames.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {prof.specialtyNames.map((name, i) => (
                          <Chip key={i} label={name} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        ))}
                      </Box>
                    ) : prof.specialtyName ? (
                      <Chip label={prof.specialtyName} size="small" color="primary" variant="outlined" />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {prof.clinicNames && prof.clinicNames.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {prof.clinicNames.map((name, i) => (
                          <Chip key={i} label={name} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        ))}
                      </Box>
                    ) : prof.clinicName ? (
                      <Typography variant="body2">{prof.clinicName}</Typography>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={prof.active !== false ? 'Activo' : 'Inactivo'}
                      size="small"
                      color={prof.active !== false ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => openEdit(prof)} color="primary">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(prof.id)} color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Dialog Crear/Editar */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? 'Editar Profesional' : 'Nuevo Profesional'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                label="Nombre(s)" value={form.nombre} required fullWidth
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                label="Apellidos" value={form.apellidos} required fullWidth
                onChange={(e) => setForm({ ...form, apellidos: e.target.value })}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                label="RUT" value={form.rut} fullWidth
                placeholder="12345678-9"
                onChange={(e) => setForm({ ...form, rut: e.target.value })}
              />
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Especialidades (múltiples)</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                {specialties.map((s) => {
                  const isSelected = form.specialtyIds.includes(s.id);
                  return (
                    <Chip
                      key={s.id}
                      label={s.name}
                      onClick={() => {
                        if (isSelected) {
                          setForm({ ...form, specialtyIds: form.specialtyIds.filter((id) => id !== s.id) });
                        } else {
                          setForm({ ...form, specialtyIds: [...form.specialtyIds, s.id] });
                        }
                      }}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      clickable
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>
              {form.specialtyIds.length === 0 && (
                <Typography variant="caption" color="text.secondary">Sin especialidades seleccionadas</Typography>
              )}
            </Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Clínicas (múltiples)</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                {clinics.map((c) => {
                  const isSelected = form.clinicIds.includes(c.id);
                  return (
                    <Chip
                      key={c.id}
                      label={c.name}
                      onClick={() => {
                        if (isSelected) {
                          setForm({ ...form, clinicIds: form.clinicIds.filter((id) => id !== c.id) });
                        } else {
                          setForm({ ...form, clinicIds: [...form.clinicIds, c.id] });
                        }
                      }}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      clickable
                      sx={{ cursor: 'pointer' }}
                    />
                  );
                })}
              </Box>
              {form.clinicIds.length === 0 && (
                <Typography variant="caption" color="text.secondary">Sin clínicas seleccionadas</Typography>
              )}
            </Grid>
            <Grid size={12}>
              <Divider sx={{ my: 1 }} />
              <FormControlLabel
                control={<Switch checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} color="success" />}
                label={form.active ? 'Profesional activo (visible en agendamiento)' : 'Profesional inactivo'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={handleClose} variant="outlined">Cancelar</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : (editingId ? 'Guardar cambios' : 'Crear profesional')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageProfessionals;
